import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { ArrowLeft, Mail, Send } from 'lucide-react';
import Footer from '@/components/Footer';

const LOGO_URL = '/assets/hero/logo.png';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactUs = () => {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !message.trim()) {
      toast.error('Please fill in name, email, and message');
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, { name, email, subject, message });
      toast.success("Thanks! We'll get back to you soon.");
      setName('');
      setEmail('');
      setSubject('');
      setMessage('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50 flex flex-col">
      <Helmet>
        <title>Contact Us | Ourmend</title>
        <meta name="description" content="Get in touch with the Ourmend team. We're here to help patients and healers." />
      </Helmet>

      {/* Header */}
      <nav className="bg-white/80 backdrop-blur-lg border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center space-x-2 sm:space-x-3" data-testid="contact-logo-link">
            <img src={LOGO_URL} alt="Ourmend Logo" className="h-9 w-9 sm:h-12 sm:w-12 object-contain" />
            <div className="text-left">
              <span className="text-lg sm:text-2xl font-bold" style={{ color: '#8B4513' }}>Ourmend</span>
              <p className="text-[10px] sm:text-xs text-stone-600 font-bold">Community Healers. Cultural Care.</p>
            </div>
          </button>
          <Button variant="outline" onClick={() => navigate(-1)} data-testid="contact-back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        </div>
      </nav>

      {/* Main */}
      <main className="flex-1 px-4 sm:px-6 py-10 sm:py-16">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8 sm:mb-10">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-amber-100 mb-4">
              <Mail className="w-7 h-7 text-[#8B4513]" />
            </div>
            <h1 className="text-3xl sm:text-5xl font-bold mb-3" style={{ color: '#3E2723' }}>Contact Us</h1>
            <p className="text-base sm:text-lg text-stone-700 max-w-xl mx-auto">
              Questions, feedback, or partnership inquiries? We'd love to hear from you.
            </p>
          </div>

          <Card className="p-6 sm:p-10 bg-white shadow-lg">
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="contact-name">Your Name</Label>
                  <Input
                    id="contact-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Jane Doe"
                    required
                    data-testid="contact-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="contact-email">Email</Label>
                  <Input
                    id="contact-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    data-testid="contact-email-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="contact-subject">Subject (optional)</Label>
                <Input
                  id="contact-subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="How can we help?"
                  data-testid="contact-subject-input"
                />
              </div>

              <div>
                <Label htmlFor="contact-message">Message</Label>
                <Textarea
                  id="contact-message"
                  rows={6}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Tell us a bit more..."
                  required
                  data-testid="contact-message-input"
                />
              </div>

              <Button
                type="submit"
                disabled={sending}
                className="w-full h-12 bg-[#8B4513] hover:bg-[#6B3410] text-white text-base font-medium"
                data-testid="contact-submit-btn"
              >
                {sending ? (
                  'Sending...'
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Message
                  </>
                )}
              </Button>

              <p className="text-xs text-stone-500 text-center">
                We typically respond within 1-2 business days.
              </p>
            </form>
          </Card>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ContactUs;
